import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import {
  DashboardBasicResult,
  DashboardCopyRequest,
  DashboardDataRequest,
  DashboardDataResponse,
  DashboardDefinition,
  DashboardFilterOptionsResponse,
  DashboardLayoutSaveRequest,
  DashboardLandingPreferenceResponse,
  DashboardMetricDefinition,
  DashboardNavigationResponse,
  DashboardPersistenceResult,
  DashboardSaveRequest,
  DashboardSummary,
  DashboardWidgetDrilldownRequest,
  DashboardWidgetDrilldownResponse,
  DashboardWidgetSaveRequest
} from '../models/Dashboards/dashboard.models';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private readonly httpOptions = {
    withCredentials: true
  };

  constructor(private httpClient: HttpClient) {}

  private navigationRequest?: Observable<DashboardNavigationResponse>;

  getDashboards(): Observable<DashboardSummary[]> {
    return this.httpClient.get<DashboardSummary[]>(`${environment.apiUrl}dashboards`, this.httpOptions);
  }

  getNavigation(refresh = false): Observable<DashboardNavigationResponse> {
    if (refresh || !this.navigationRequest) {
      this.navigationRequest = this.httpClient.get<DashboardNavigationResponse>(
        `${environment.apiUrl}dashboards/navigation`,
        this.httpOptions
      ).pipe(shareReplay(1));
    }
    return this.navigationRequest;
  }

  getLandingRoute(refresh = false): Observable<'dashboards' | 'home'> {
    return this.getNavigation(refresh).pipe(
      map(result => result.HasAccessibleDashboard && result.LandingPageID === 2 ? 'dashboards' : 'home'),
      catchError(() => of('home' as const))
    );
  }

  getLandingPreference(userId?: number): Observable<DashboardLandingPreferenceResponse> {
    const suffix = userId == null ? '' : `/${userId}`;
    return this.httpClient.get<DashboardLandingPreferenceResponse>(
      `${environment.apiUrl}dashboards/navigation/preferences${suffix}`, this.httpOptions
    );
  }

  saveLandingPreference(landingPageId: number | null, userId?: number): Observable<DashboardBasicResult> {
    const suffix = userId == null ? '' : `/${userId}`;
    return this.httpClient.put<DashboardBasicResult>(
      `${environment.apiUrl}dashboards/navigation/preferences${suffix}`,
      { LandingPageID: landingPageId }, this.httpOptions
    ).pipe(tap(result => { if (!result.IsError) this.clearNavigationCache(); }));
  }

  clearNavigationCache(): void {
    this.navigationRequest = undefined;
  }

  getDashboard(dashboardId: number): Observable<DashboardDefinition> {
    return this.httpClient.get<DashboardDefinition>(`${environment.apiUrl}dashboards/${dashboardId}`, this.httpOptions);
  }

  getMetricCatalogue(): Observable<DashboardMetricDefinition[]> {
    return this.httpClient.get<DashboardMetricDefinition[]>(`${environment.apiUrl}dashboards/metrics`, this.httpOptions);
  }

  getFilterOptions(dashboardId: number): Observable<DashboardFilterOptionsResponse> {
    return this.httpClient.get<DashboardFilterOptionsResponse>(
      `${environment.apiUrl}dashboards/${dashboardId}/filters`,
      this.httpOptions
    );
  }

  getDashboardData(request: DashboardDataRequest): Observable<DashboardDataResponse> {
    return this.httpClient.post<DashboardDataResponse>(`${environment.apiUrl}dashboards/data`, request, this.httpOptions);
  }

  getWidgetDrilldown(request: DashboardWidgetDrilldownRequest): Observable<DashboardWidgetDrilldownResponse> {
    return this.httpClient.post<DashboardWidgetDrilldownResponse>(`${environment.apiUrl}dashboards/drilldown`, request, this.httpOptions);
  }

  createDashboard(request: DashboardSaveRequest): Observable<DashboardPersistenceResult> {
    return this.httpClient.post<DashboardPersistenceResult>(`${environment.apiUrl}dashboards`, request, this.httpOptions);
  }

  updateDashboard(dashboardId: number, request: DashboardSaveRequest): Observable<DashboardPersistenceResult> {
    return this.httpClient.put<DashboardPersistenceResult>(`${environment.apiUrl}dashboards/${dashboardId}`, request, this.httpOptions);
  }

  copyDashboard(request: DashboardCopyRequest): Observable<DashboardPersistenceResult> {
    return this.httpClient.post<DashboardPersistenceResult>(`${environment.apiUrl}dashboards/copy`, request, this.httpOptions);
  }

  deleteDashboard(dashboardId: number): Observable<DashboardBasicResult> {
    return this.httpClient.delete<DashboardBasicResult>(`${environment.apiUrl}dashboards/${dashboardId}`, this.httpOptions);
  }

  addWidget(dashboardId: number, request: DashboardWidgetSaveRequest): Observable<DashboardPersistenceResult> {
    return this.httpClient.post<DashboardPersistenceResult>(`${environment.apiUrl}dashboards/${dashboardId}/widgets`, request, this.httpOptions);
  }

  updateWidget(dashboardId: number, dashboardWidgetId: number, request: DashboardWidgetSaveRequest): Observable<DashboardPersistenceResult> {
    return this.httpClient.put<DashboardPersistenceResult>(`${environment.apiUrl}dashboards/${dashboardId}/widgets/${dashboardWidgetId}`, request, this.httpOptions);
  }

  removeWidget(dashboardId: number, dashboardWidgetId: number): Observable<DashboardBasicResult> {
    return this.httpClient.delete<DashboardBasicResult>(`${environment.apiUrl}dashboards/${dashboardId}/widgets/${dashboardWidgetId}`, this.httpOptions);
  }

  saveDashboardLayout(request: DashboardLayoutSaveRequest): Observable<DashboardBasicResult> {
    return this.httpClient.put<DashboardBasicResult>(`${environment.apiUrl}dashboards/${request.DashboardID}/layout`, request, this.httpOptions);
  }
}
